import {
  DashboardFilterDataType,
  DashboardFilterValue,
  GlobalFilters,
} from "../models/Dashboard";

const DASHBOARD_FILTER_KEYS = {
  developerUniqueTags: "DeveloperUniqueTags",
  programUniqueTags: "ProgramUniqueTags",
  merchantGroupUniqueTags: "MerchantGroupUniqueTags",
} as const;

const RESERVED_KEYS = new Set<string>([
  ...Object.values(DASHBOARD_FILTER_KEYS),
]);
const LEGACY_REMOVED_KEYS = new Set<string>([
  "StartDate",
  "EndDate",
  "MerchantIds",
  "IcaIds",
  "BinIds",
  "CountryCodes",
]);

const DATA_TYPES: DashboardFilterDataType[] = [
  "string",
  "int",
  "decimal",
  "bool",
  "date",
  "json",
];

const isDataType = (value: unknown): value is DashboardFilterDataType =>
  typeof value === "string" && DATA_TYPES.includes(value as DashboardFilterDataType);

const toText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "boolean" || typeof value === "number") return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const parseByType = (
  dataType: DashboardFilterDataType,
  filterValue: string
): unknown => {
  const trimmed = filterValue.trim();
  if (!trimmed) return "";

  switch (dataType) {
    case "int": {
      const parsed = parseInt(trimmed, 10);
      return Number.isNaN(parsed) ? trimmed : parsed;
    }
    case "decimal": {
      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? trimmed : parsed;
    }
    case "bool": {
      if (/^(true|1|yes|y)$/i.test(trimmed)) return true;
      if (/^(false|0|no|n)$/i.test(trimmed)) return false;
      return trimmed;
    }
    case "date":
      return trimmed;
    case "json": {
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    }
    case "string":
    default:
      return filterValue;
  }
};

const normalizeRow = (row: Record<string, unknown>): DashboardFilterValue | null => {
  const filterKey = toText(row.filterKey ?? row.FilterKey).trim();
  if (!filterKey) return null;
  if (LEGACY_REMOVED_KEYS.has(filterKey)) return null;

  const dataTypeRaw = row.dataType ?? row.DataType;
  const dataType: DashboardFilterDataType = isDataType(dataTypeRaw)
    ? dataTypeRaw
    : "string";

  const filterValue = toText(row.filterValue ?? row.FilterValue);

  return {
    filterKey,
    filterValue,
    dataType,
  };
};

export const normalizeGlobalFilters = (raw: unknown): GlobalFilters => {
  if (!raw || typeof raw !== "object") return {};

  const candidate = raw as Record<string, unknown>;

  const rowsFromArray = Array.isArray(raw)
    ? raw
    : Array.isArray(candidate.dashboardFilters)
    ? candidate.dashboardFilters
    : Array.isArray(candidate.customFilters)
    ? candidate.customFilters
    : null;

  if (!rowsFromArray) {
    return candidate as GlobalFilters;
  }

  const rows = rowsFromArray
    .map((item) => (typeof item === "object" && item ? normalizeRow(item as Record<string, unknown>) : null))
    .filter((item): item is DashboardFilterValue => Boolean(item));

  return buildGlobalFiltersFromRows(rows);
};

export const extractFilterRows = (filters: GlobalFilters): DashboardFilterValue[] => {
  const rows: DashboardFilterValue[] = [];

  const addArrayRow = (filterKey: string, values?: string[]) => {
    if (!values || values.length === 0) return;
    rows.push({
      filterKey,
      filterValue: JSON.stringify(values),
      dataType: "json",
    });
  };

  addArrayRow(
    DASHBOARD_FILTER_KEYS.programUniqueTags,
    filters.entities?.programUniqueTags
  );
  addArrayRow(
    DASHBOARD_FILTER_KEYS.developerUniqueTags,
    filters.entities?.developerUniqueTags
  );
  addArrayRow(
    DASHBOARD_FILTER_KEYS.merchantGroupUniqueTags,
    filters.entities?.merchantGroupUniqueTags
  );

  if (Array.isArray(filters.customFilters)) {
    rows.push(
      ...filters.customFilters
        .filter((row) => !RESERVED_KEYS.has(row.filterKey))
        .map((row) => ({ ...row }))
    );
  }

  const extraRows = Object.entries(filters)
    .filter(([key]) => key !== "entities")
    .filter(([key]) => key !== "customFilters")
    .filter(([key]) => !RESERVED_KEYS.has(key))
    .filter(([key]) => !LEGACY_REMOVED_KEYS.has(key))
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([filterKey, value]) => {
      if (typeof value === "number") {
        return {
          filterKey,
          filterValue: String(value),
          dataType: Number.isInteger(value) ? "int" : "decimal",
        } as DashboardFilterValue;
      }

      if (typeof value === "boolean") {
        return {
          filterKey,
          filterValue: String(value),
          dataType: "bool",
        } as DashboardFilterValue;
      }

      if (typeof value === "object") {
        return {
          filterKey,
          filterValue: toText(value),
          dataType: "json",
        } as DashboardFilterValue;
      }

      return {
        filterKey,
        filterValue: toText(value),
        dataType: "string",
      } as DashboardFilterValue;
    });

  rows.push(...extraRows);

  return rows;
};

export const buildGlobalFiltersFromRows = (
  rows: DashboardFilterValue[]
): GlobalFilters => {
  const sanitized = rows
    .map((row) => ({
      filterKey: row.filterKey.trim(),
      filterValue: row.filterValue,
      dataType: row.dataType,
    }))
    .filter((row) => row.filterKey.length > 0)
    .filter((row) => row.filterValue.trim().length > 0);

  const customRows: DashboardFilterValue[] = [];

  const getJsonStringArray = (key: string): string[] => {
    const row = sanitized.find((item) => item.filterKey === key);
    if (!row) return [];
    const value = parseByType("json", row.filterValue);
    return Array.isArray(value)
      ? value.map((item) => String(item)).filter((item) => item.length > 0)
      : [];
  };

  const entities = {
    developerUniqueTags: getJsonStringArray(DASHBOARD_FILTER_KEYS.developerUniqueTags),
    programUniqueTags: getJsonStringArray(DASHBOARD_FILTER_KEYS.programUniqueTags),
    merchantGroupUniqueTags: getJsonStringArray(DASHBOARD_FILTER_KEYS.merchantGroupUniqueTags),
  };

  const result: GlobalFilters = {};

  const hasEntities = Object.values(entities).some((values) => values.length > 0);
  if (hasEntities) {
    result.entities = {};
    if (entities.developerUniqueTags.length > 0) {
      result.entities.developerUniqueTags = entities.developerUniqueTags;
    }
    if (entities.programUniqueTags.length > 0) {
      result.entities.programUniqueTags = entities.programUniqueTags;
    }
    if (entities.merchantGroupUniqueTags.length > 0) {
      result.entities.merchantGroupUniqueTags = entities.merchantGroupUniqueTags;
    }
  }

  sanitized.forEach((row) => {
    if (RESERVED_KEYS.has(row.filterKey)) {
      return;
    }
    if (LEGACY_REMOVED_KEYS.has(row.filterKey)) {
      return;
    }

    customRows.push(row);
    result[row.filterKey] = parseByType(row.dataType, row.filterValue);
  });

  if (customRows.length > 0) {
    result.customFilters = customRows;
  }

  return result;
};

export const toDashboardFilterRowsPayload = (
  rows: DashboardFilterValue[]
): Array<{ filterKey: string; filterValue: string; dataType: DashboardFilterDataType }> =>
  rows
    .map((row) => ({
      filterKey: row.filterKey.trim(),
      filterValue: row.filterValue,
      dataType: row.dataType,
    }))
    .filter((row) => row.filterKey.length > 0)
    .filter((row) => row.filterValue.trim().length > 0);
